import React from 'react';
import PropTypes from 'prop-types';
import {Control, Form} from 'react-redux-form';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';

import {actions as customerActions} from '../../ducks/customer';
import {actions as mmsSettingsActions} from '../../ducks/mmsSettings';
import ControlGroup from '../forms/ControlGroup';
import TimeSelector from '../forms/TimeSelector';
import PageHeader from '../PageHeader';
import {duckify, singleDuck} from "../../utils/duckify";
import { flashMessages, modals, users as usersDuck } from '../../ducks';
import FlashRoot from "components/FlashMessage/FlashRoot";
import formatPhoneNumber from  "../../utils/phoneFormat";
import './styles/mms.scss';
import {RequiresSuperAdmin} from '../shared/RequiresUser';
import ValidationErrors from '../forms/ValidationErrors';

class MMSSetup extends React.Component {
  static propTypes = {
    apiHealth: PropTypes.string,
    model: PropTypes.object,

    checkApiHealth: PropTypes.func,
    load: PropTypes.func,
    save: PropTypes.func
  };

  static defaultProps = {
    apiHealth: '',
    model: {},
    currentUser: {},

    checkApiHealth: () => false,
    load: () => false,
    save: () => false
  };

  constructor() {
    super();

    this.state = {
      errors: {}
    }

    this.setErrors = this.setErrors.bind(this);
  }

  setErrors(e) {
    this.setState({
      errors: e.errors
    })
  }

  render() {
    const {apiHealth, model, currentUser, save, dispatch} = this.props;

    const apiHealthInfo = () => {
      let tooltip;

      switch(apiHealth) {
        case 'degraded':
          tooltip = <Tooltip id={apiHealth}>
            <strong>Degraded</strong>: Some users may experience interruptions processing
            pending outbound text messages. There is no action required, and the pending
            messages will be sent once connectivity issues have been resolved.
          </Tooltip>;

          return <div>
            <OverlayTrigger
              placement="right"
              overlay={tooltip}
              title="Degraded"
            >
              <i className="fa fa-circle orange"/>
            </OverlayTrigger>
          </div>;
        case 'down':
          tooltip = <Tooltip id={apiHealth}>
            <strong>Down</strong>: At this time, we are unable to process pending outbound
            text messages. There is no action required, and the pending messages will be
            sent once connectivity issues have been resolved.
          </Tooltip>;

          return <OverlayTrigger
            placement="right"
            overlay={tooltip}
            title="Down"
          >
            <i className="fa fa-circle red"/>
          </OverlayTrigger>;
        default:
          tooltip = <Tooltip id={apiHealth}>
            <strong>Up</strong>: There are no known service interruptions.
          </Tooltip>;

          return <OverlayTrigger
            placement="right"
            overlay={tooltip}
            title="Up"
          >
            <i className="fa fa-circle green"/>
          </OverlayTrigger>;
      }
    };


    const resetNumber = () => {
      dispatch(modals.actions.show('VerificationModal', {
        title: 'Reset Text Message Number',
        message: (
          <div>
            <p>
              This will de-provision the customer's existing text messaging phone number and create a new one when their
              next text message is sent.
            </p>
            <p>
              Do not delete numbers that were banned from carriers for spamming unless the cause of the spamming was
              identified, corrective actions have been taken to prevent recurrence, and requests to the carrier to
              de-ban were not effective. Creating a new number and getting it re-banned risks ALL CUSTOMER ACCOUNTS.
              Do you want to continue anyway?
            </p>
          </div>
        ),
        confirmButtonType: 'danger',
        onConfirm: () => {
          dispatch(customerActions.deleteMmsPhoneNumber())
          .then((response) => dispatch(mmsSettingsActions.load(response.customer)))
          .then(() => dispatch(modals.actions.hide()))
          .then(() =>
            dispatch(flashMessages.actions.show({
              message: "Settings were saved successfully.",
              type: 'success',
              dismissible: true
            }))
          );
        },
        onClose: () => {
          dispatch(modals.actions.hide());
        }
      }));
    };

    const phoneNumber = () => {
      if (model.mms_phone_number) {
        return (
          <React.Fragment>
            <div className='mms-phone-display'>
              {formatPhoneNumber(model.mms_phone_number)}
            </div>
            <RequiresSuperAdmin>
              <button type='button' className={'btn btn-danger'} onClick={() => resetNumber()}>
                Reset
              </button>
            </RequiresSuperAdmin>
          </React.Fragment>
        );
      }
      else if (model.customer_type === 'production') {
        return (
          <React.Fragment>
            <div className='alert alert-info'>
              Phone number will be generated if/when the first text message is sent.
            </div>
          </React.Fragment>
        );
      }

      return (
        <React.Fragment>
          <div className='mms-phone-display'>
            {formatPhoneNumber(model.mms_phone_number_with_default)}
          </div>
          <div className='alert alert-info'>
            Non-production customers send text messages from the default phone number.
          </div>
        </React.Fragment>
      );
    };

    return <div>
      <PageHeader header="Text Messaging Setup"/>
      <FlashRoot/>

      <div className='alert alert-warning'>
        <strong>Note:</strong> Finalsite Enrollment's text messaging system is designed for parent
        communication within the inquiry, admissions, or enrollment process.

        Finalsite Enrollment text messaging is <strong>not</strong> designed or intended to be used
        as part of an emergency alert or crisis management system.
      </div>

      <p className='help-block'>
        In order to prevent sending MMS messages to recipients during off hours, any
        messages sent outside of the below time window will be held until the blackout
        period has ended.
      </p>

      <Form
        model="mmsSettings.model"
        className="form-horizontal mms-setup"
        onSubmit={(values) => save(values, this.setErrors)}
      >
        <ValidationErrors errors={this.state.errors}/>
        <ControlGroup label="MMS window">
          <div className='row'>
            <div className='col-auto'>
              <TimeSelector model=".mms_start" modelValue={model.mms_start} className="form-select form-select-sm adjust-time-selector"/>
            </div>
            <div className='col-auto'><div className='form-text text-center'>to</div></div>
            <div className='col-auto'>
              <TimeSelector model=".mms_stop" modelValue={model.mms_stop} className="form-select form-select-sm adjust-time-selector"/>
            </div>
          </div>
        </ControlGroup>

        <ControlGroup label="API Health">
          <div className="text">{apiHealthInfo()}</div>
        </ControlGroup>

        <ControlGroup label="Phone Number">
          <div className='row'>
            <div className='col-auto'>
              {phoneNumber()}
            </div>
          </div>
        </ControlGroup>

        <Control.button
          model="mmsSettings.model"
          disabled={{pending: true}}
          className="btn btn-primary"
          type="submit"
        >
          Submit
        </Control.button>

      </Form>
    </div>;
  }

  componentDidMount() {
    const {checkApiHealth, load} = this.props;

    load();
    checkApiHealth();
  }
}

const mapStateToProps = (state) => {
  return {
    ...state.mmsSettings
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    checkApiHealth: () => dispatch(mmsSettingsActions.checkApiHealth()),
    load: () =>
      dispatch(customerActions.load())
        .then((response) =>
          dispatch(mmsSettingsActions.load(response.customer))),
    save: (values, setErrors) => dispatch(mmsSettingsActions.save(values))
      .then(() => {
        setErrors({});
        dispatch(flashMessages.actions.show({
          message: "Settings were saved successfully.",
          type: 'success',
          dismissible: true
        }));
      })
      .catch((error) => {
        if (error) {
          setErrors(error);
        }
      })
  }
};

const mapDucksToProps = () => ({
  currentUser: singleDuck(usersDuck, 'current')
});


export default duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(MMSSetup);
